// src/modules/operaciones/inventarios/dashboard/ChartTooltip.js
//
// Tooltip reutilizable para Recharts (barras, torta, líneas, dispersión, compuestos...).
// Tarjeta blanca con sombra suave y bordes redondeados; un renglón por serie con su color.
//
// USO BÁSICO
//   <Tooltip content={<ChartTooltip />} />                       // formato automático por nombre
//   <Tooltip content={<ChartTooltip formato="moneda" />} />      // todo en $ (torta con {name, value})
//   <Tooltip content={<ChartTooltip formato="compacto" />} />    // $10,5 mil M
//
// FORMATO
//   formato: "moneda" | "compacto" | "numero" | "porcentaje"        → para todas las series
//            { venta: "moneda", acumuladoPct: "porcentaje" }        → por serie (dataKey)
//   Sin `formato`, el tipo se deduce del nombre de la serie:
//     pct / porcentaje / participacion  → porcentaje
//     cantidad / articulos / rank...    → número
//     venta / perdida / merma / neta / conocida / desconocida / faltantes / sobrantes / valor... → moneda
//   Prioridad: mapa por serie  >  nombre que indica porcentaje  >  `formato` global  >  deducido.
//   (Así un Pareto con barras en $ y línea en % se formatea bien con formato="moneda".)
//
// OTRAS OPCIONES
//   etiquetas:     { desconocida: "Merma desconocida" }  → nombre visible de cada serie
//   ocultar:       ["base", "inicio"]                    → series que no se muestran (p. ej. la barra
//                                                          invisible del puente/waterfall)
//   titulo:        string | (label, fila) => string      → título de la tarjeta
//   formatoLabel:  (label) => string                     → formatea el eje X (una fecha, un truncado...)
//   colorearSigno: true                                  → rojo si el valor es negativo (útil en mermas)
//   mostrarTotal:  true                                  → suma las series (barras apiladas)
//   filasExtra:    (fila) => [{ etiqueta, valor, formato?, color? }]  → renglones calculados
//                  Ej. puente: (f) => [{ etiqueta: "Acumulado", valor: f.fin, formato: "moneda" }]
//
// Si el dato trae `pct` (como la dona de familias) y no es la serie mostrada, se agrega
// automáticamente el renglón "Participación".

import React from "react";
import { fmtMoneda, fmtCompacto, fmtNumero, fmtPorcentaje, COLORES_SEMANTICOS } from "../utils/informesComun";

/* ========================================================================== */
/* Formato                                                                    */
/* ========================================================================== */

const RE_PORCENTAJE = /pct|porcentaje|percent|participacion/i;
const RE_NUMERO = /cantidad|articulos|unidades|filas|rank|^z$|^n$|conteo/i;
const RE_MONEDA =
  /venta|perdida|merma|neta|neto|desconocida|conocida|faltante|sobrante|valor|entrada|salida|costo|precio|ganancia|total|size|monto|importe|inicio|fin|\$/i;

// Deduce el formato por el nombre de la serie.
export function detectarFormato(clave) {
  const k = String(clave ?? "");
  if (RE_PORCENTAJE.test(k)) return "porcentaje";
  if (RE_NUMERO.test(k)) return "numero";
  if (RE_MONEDA.test(k)) return "moneda";
  return "numero";
}

// Convierte un valor al texto que se muestra.
export function formatearValor(valor, formato = "numero") {
  if (valor === null || valor === undefined || valor === "") return "-";
  if (typeof valor !== "number") {
    const n = Number(valor);
    if (!Number.isFinite(n)) return String(valor);
    valor = n;
  }
  switch (formato) {
    case "moneda":
      return fmtMoneda(valor);
    case "compacto":
      return fmtCompacto(valor);
    case "porcentaje":
      return fmtPorcentaje(valor, 2);
    default:
      return fmtNumero(valor);
  }
}

// "perdidaBruta" → "Perdida Bruta"
function nombreLegible(clave) {
  const t = String(clave ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function resolverFormato(clave, formato) {
  if (formato && typeof formato === "object" && formato[clave]) return formato[clave];
  const auto = detectarFormato(clave);
  if (auto === "porcentaje") return "porcentaje";
  if (typeof formato === "string" && formato) return formato;
  return auto;
}

/* ========================================================================== */
/* Utilidades de payload                                                      */
/* ========================================================================== */

// Series que no deben mostrarse: marcadas para ocultar, sin valor o transparentes (barra "fantasma").
function esVisible(entry, ocultar) {
  if (!entry) return false;
  const clave = entry.dataKey ?? entry.name;
  if (entry.hide) return false;
  if (typeof clave === "string" && ocultar.includes(clave)) return false;
  if (typeof entry.name === "string" && ocultar.includes(entry.name)) return false;
  const color = String(entry.color ?? entry.fill ?? "").toLowerCase();
  if (color === "transparent" || color === "none") return false;
  if (entry.value === null || entry.value === undefined) return false;
  return true;
}

const colorDe = (entry) => entry.color ?? entry.payload?.fill ?? entry.fill ?? COLORES_SEMANTICOS.neutro;

// Título: el `label` del eje X o, en tortas/dispersión, el nombre del dato.
function tituloDe(label, fila, titulo, formatoLabel) {
  if (typeof titulo === "function") return titulo(label, fila);
  if (typeof titulo === "string") return titulo;
  const base = label ?? fila?.name ?? fila?.nombre ?? fila?.etiqueta ?? fila?.descripcion ?? null;
  if (base === null || base === undefined || base === "") return null;
  return typeof formatoLabel === "function" ? formatoLabel(base) : String(base);
}

/* ========================================================================== */
/* Componente                                                                 */
/* ========================================================================== */

export function ChartTooltip({
  active,
  payload,
  label,
  formato = null,
  etiquetas = {},
  ocultar = [],
  titulo = null,
  formatoLabel = null,
  colorearSigno = false,
  mostrarTotal = false,
  filasExtra = null,
}) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;

  const visibles = payload.filter((e) => esVisible(e, ocultar));
  const fila = payload[0]?.payload || null;

  const renglones = visibles.map((entry, i) => {
    const clave = entry.dataKey ?? entry.name ?? `serie-${i}`;
    const claveTexto = typeof clave === "function" ? entry.name : clave;
    const fmt = resolverFormato(claveTexto, formato);
    return {
      id: `${claveTexto}-${i}`,
      etiqueta: etiquetas[claveTexto] ?? etiquetas[entry.name] ?? entry.name ?? nombreLegible(claveTexto),
      valor: entry.value,
      formato: fmt,
      color: colorDe(entry),
      claveTexto,
    };
  });

  // Participación (dona): si el dato trae `pct` y esa no es la serie mostrada
  if (
    fila &&
    typeof fila.pct === "number" &&
    renglones.length > 0 &&
    !renglones.some((r) => RE_PORCENTAJE.test(String(r.claveTexto)))
  ) {
    renglones.push({
      id: "pct-auto",
      etiqueta: "Participación",
      valor: fila.pct,
      formato: "porcentaje",
      color: COLORES_SEMANTICOS.neutro,
      claveTexto: "pct",
    });
  }

  // Renglones calculados por el gráfico
  if (typeof filasExtra === "function" && fila) {
    (filasExtra(fila) || []).forEach((x, i) => {
      if (!x || x.valor === null || x.valor === undefined) return;
      renglones.push({
        id: `extra-${i}`,
        etiqueta: x.etiqueta,
        valor: x.valor,
        formato: x.formato || detectarFormato(x.etiqueta),
        color: x.color || COLORES_SEMANTICOS.neutro,
        claveTexto: x.etiqueta,
      });
    });
  }

  const encabezado = tituloDe(label, fila, titulo, formatoLabel);
  if (renglones.length === 0 && !encabezado) return null;

  // Total de las series numéricas (barras apiladas)
  let total = null;
  if (mostrarTotal) {
    const numericas = visibles.filter((e) => typeof e.value === "number" && Number.isFinite(e.value));
    if (numericas.length > 1) {
      const fmtTotal = resolverFormato(numericas[0].dataKey ?? numericas[0].name, formato);
      total = { valor: numericas.reduce((s, e) => s + e.value, 0), formato: fmtTotal };
    }
  }

  const estiloValor = (r) =>
    colorearSigno && typeof r.valor === "number" && r.valor < 0 && r.formato !== "porcentaje"
      ? { color: COLORES_SEMANTICOS.perdidaOscuro }
      : undefined;

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2 text-xs text-gray-700"
      style={{ maxWidth: 280, pointerEvents: "none" }}
      role="tooltip"
    >
      {encabezado && (
        <p className="font-semibold text-gray-900 mb-1 break-words leading-snug">{encabezado}</p>
      )}

      <ul className="space-y-0.5">
        {renglones.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 min-w-0">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: r.color }}
                aria-hidden="true"
              />
              <span className="text-gray-500 truncate">{r.etiqueta}</span>
            </span>
            <span className="font-semibold tabular-nums whitespace-nowrap text-gray-900" style={estiloValor(r)}>
              {formatearValor(r.valor, r.formato)}
            </span>
          </li>
        ))}
      </ul>

      {total && (
        <div className="flex items-center justify-between gap-4 mt-1.5 pt-1.5 border-t border-gray-100">
          <span className="text-gray-500">Total</span>
          <span
            className="font-semibold tabular-nums whitespace-nowrap text-gray-900"
            style={estiloValor({ valor: total.valor, formato: total.formato })}
          >
            {formatearValor(total.valor, total.formato)}
          </span>
        </div>
      )}
    </div>
  );
}

export default ChartTooltip;